import React, { useState, useEffect, useCallback } from 'react';
import { getReports, getReportStats, getClientTransactions, getGateways, refundTransaction } from '../api';

export default function ReportsTab() {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchFirstName, setSearchFirstName] = useState('');
  const [searchLastName, setSearchLastName] = useState('');
  const [searchAddress, setSearchAddress] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchCard, setSearchCard] = useState('');
  const [searchFirst6, setSearchFirst6] = useState('');
  const [searchLast4, setSearchLast4] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searchCustomerId, setSearchCustomerId] = useState('');
  const [appliedSearch, setAppliedSearch] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilters, setTypeFilters] = useState([]);
  const [selectedGateways, setSelectedGateways] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [gateways, setGateways] = useState([]);
  const [showGwPicker, setShowGwPicker] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientData, setClientData] = useState(null);

  useEffect(() => { getGateways().then(r => { if (r.success) setGateways(r.data || []); }).catch(() => {}); }, []);

  // appliedFilters stores ALL filters — only updated on Search click
  const [appliedFilters, setAppliedFilters] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      Object.entries(appliedFilters).forEach(([k, v]) => { if (v) p.set(k, String(v)); });
      p.set('limit', perPage); p.set('offset', page * perPage);
      const [txR, stR] = await Promise.all([getReports(p.toString()), getReportStats(p.toString())]);
      if (txR.success) { setTransactions(txR.data.transactions || []); setTotal(txR.data.total || 0); }
      if (stR.success) setStats(stR.data || {});
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [appliedFilters, perPage, page]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [appliedFilters, perPage]);

  const handleSearch = () => {
    setAppliedFilters({
      first_name: searchFirstName.trim(), last_name: searchLastName.trim(), address: searchAddress.trim(),
      email: searchEmail.trim(), card: searchCard.trim(), first6: searchFirst6.trim(), last4: searchLast4.trim(),
      phone: searchPhone.trim(), customer_id: searchCustomerId.trim(),
      status: statusFilter,
      types: typeFilters.length > 0 ? typeFilters.join(',') : '',
      gateway_ids: selectedGateways.length > 0 ? selectedGateways.join(',') : '',
      date_from: dateFrom, date_to: dateTo,
    });
  };
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

  const handleClientClick = async (clientId) => {
    if (selectedClient === clientId) { setSelectedClient(null); setClientData(null); return; }
    try {
      const res = await getClientTransactions(clientId);
      if (res.success) { setSelectedClient(clientId); setClientData(res.data); }
    } catch (err) { console.error(err); }
  };

  const toggleGw = (id) => setSelectedGateways(p => p.includes(id) ? p.filter(g => g !== id) : [...p, id]);
  const fmt = (v) => parseFloat(v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const fmtD = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const totalPages = Math.ceil(total / perPage);
  const sBadge = (s) => { const c = { success: 'bg-green-500/20 text-green-400', declined: 'bg-red-500/20 text-red-400', error: 'bg-orange-500/20 text-orange-400', refunded: 'bg-purple-500/20 text-purple-400' }; return <span className={`px-2 py-0.5 rounded text-xs ${c[s] || 'bg-slate-600 text-slate-300'}`}>{s}</span>; };
  const tBadge = (t) => { const c = { rebill: 'bg-blue-500/20 text-blue-400', autorb: 'bg-purple-500/20 text-purple-400', recycle: 'bg-orange-500/20 text-orange-400' }; return <span className={`px-2 py-0.5 rounded text-xs ${c[t] || 'bg-slate-600 text-slate-300'}`}>{t || 'rebill'}</span>; };
  const rBadge = (s) => { const c = { pending: 'bg-yellow-500/10 text-yellow-400', scheduled: 'bg-blue-500/10 text-blue-400', success_recovered: 'bg-emerald-500/10 text-emerald-400', failed: 'bg-red-500/10 text-red-400', ultimate_declined: 'bg-red-800/20 text-red-300' }; return <span className={`px-2 py-0.5 rounded text-xs ${c[s] || 'bg-slate-600 text-slate-300'}`}>{s}</span>; };
  const iCls = "bg-slate-700 text-white text-xs rounded px-2 py-1.5 border border-slate-600 focus:border-blue-500 outline-none w-full";

  const [refunding, setRefunding] = useState(null);

  const handleRefund = async (txId, txnIdNmi, amount) => {
    if (!window.confirm(`Refund transaction ${txnIdNmi} for ${fmt(amount)}? This action cannot be undone.`)) return;
    setRefunding(txId);
    try {
      const res = await refundTransaction(txId);
      if (res.success) {
        alert(`✅ Refund successful! Refund TxnID: ${res.data.refundTxnId}`);
        fetchData();
        if (selectedClient) { const r = await getClientTransactions(selectedClient); if (r.success) setClientData(r.data); }
      } else { alert(`❌ Refund failed: ${res.error}`); }
    } catch (err) { alert(`❌ Error: ${err.message}`); }
    finally { setRefunding(null); }
  };

  const ci = clientData?.client;
  const cTxns = clientData?.transactions || [];
  const cTrips = clientData?.triplets || [];
  const cRecycle = clientData?.recycle_entries || [];

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-white">Reports — CRM Transactions</h2>
      <p className="text-xs text-slate-400">All Rebill, AutoRb & Recycle Billing transactions (no initial transactions)</p>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[{ l: 'Total Txns', v: stats.total_transactions || 0, c: 'text-white' }, { l: 'Success', v: stats.success_count || 0, c: 'text-green-400' }, { l: 'Declined', v: stats.declined_count || 0, c: 'text-red-400' }, { l: 'Total Revenue', v: fmt(stats.total_revenue), c: 'text-green-400' }, { l: 'Approval', v: `${stats.approval_rate || 0}%`, c: 'text-blue-400' }, { l: 'Rebill CA', v: fmt(stats.rebill_revenue), c: 'text-blue-400' }, { l: 'AutoRb CA', v: fmt(stats.autorb_revenue), c: 'text-purple-400' }].map((s, i) => (
          <div key={i} className="bg-slate-800 rounded-lg p-3 border border-slate-700"><div className="text-[10px] text-slate-500 uppercase">{s.l}</div><div className={`text-lg font-bold ${s.c}`}>{s.v}</div></div>))}
      </div>

      {/* Search */}
      <div className="bg-slate-800 rounded-lg p-3 border border-slate-700 space-y-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-bold text-slate-300">🔍 Search Criteria (cumulative — click Search to apply)</span>
          <button onClick={handleSearch} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold flex items-center space-x-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <span>Search</span>
          </button>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
          {[['First Name', searchFirstName, setSearchFirstName, 'John'], ['Last Name', searchLastName, setSearchLastName, 'Doe'], ['Address', searchAddress, setSearchAddress, '123 Main'], ['Email', searchEmail, setSearchEmail, 'john@...'], ['Card Number', searchCard, setSearchCard, '4111...'], ['First 6', searchFirst6, setSearchFirst6, '411111'], ['Last 4', searchLast4, setSearchLast4, '1234'], ['Phone', searchPhone, setSearchPhone, '+1...'], ['Customer ID', searchCustomerId, setSearchCustomerId, '12345']].map(([label, val, setter, ph], i) => (
            <div key={i}><label className="text-[10px] text-slate-500 block mb-0.5">{label}</label>
              <input type="text" value={val} onChange={e => setter(e.target.value)} onKeyDown={handleKeyDown} placeholder={ph} className={iCls} /></div>))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div className="relative">
            <button onClick={() => setShowGwPicker(!showGwPicker)} className="w-full bg-slate-700 text-white text-xs rounded px-3 py-2 border border-slate-600 text-left">
              {selectedGateways.length === 0 ? 'All Gateways' : `${selectedGateways.length} GW selected`}
            </button>
            {showGwPicker && (<div className="absolute z-20 mt-1 w-72 bg-slate-700 border border-slate-600 rounded-lg shadow-xl p-2 max-h-60 overflow-y-auto">
              <div className="flex space-x-2 mb-2">
                <button onClick={() => setSelectedGateways(gateways.map(g => g.id))} className="px-2 py-1 bg-blue-600 text-white rounded text-[10px]">Select All</button>
                <button onClick={() => setSelectedGateways([])} className="px-2 py-1 bg-slate-600 text-white rounded text-[10px]">Unselect All</button>
                <button onClick={() => setShowGwPicker(false)} className="ml-auto px-2 py-1 bg-slate-500 text-white rounded text-[10px]">Close</button>
              </div>
              {gateways.map(g => (<label key={g.id} className="flex items-center space-x-2 px-2 py-1 hover:bg-slate-600 rounded cursor-pointer">
                <input type="checkbox" checked={selectedGateways.includes(g.id)} onChange={() => toggleGw(g.id)} className="rounded border-slate-500" />
                <span className="text-xs text-slate-200">{g.processor} - {g.bank} - {g.dba}</span></label>))}
            </div>)}
          </div>
          {/* Status dropdown with checkboxes */}
          <div className="relative">
            <button onClick={() => setShowStatusPicker(!showStatusPicker)}
              className="w-full bg-slate-700 text-white text-xs rounded px-3 py-2 border border-slate-600 text-left">
              {statusFilter === '' ? 'All Statuses' : statusFilter === 'success' ? '✅ Completed' : statusFilter === 'refunded' ? '↩ Refunded' : '❌ Declined'}
            </button>
            {showStatusPicker && (
              <div className="absolute z-20 mt-1 w-48 bg-slate-700 border border-slate-600 rounded-lg shadow-xl p-2">
                {[['', 'All Statuses'], ['success', '✅ Completed'], ['declined', '❌ Declined'], ['refunded', '↩ Refunded']].map(([val, label]) => (
                  <label key={val} className="flex items-center space-x-2 px-2 py-1.5 hover:bg-slate-600 rounded cursor-pointer"
                    onClick={() => { setStatusFilter(val); setShowStatusPicker(false); }}>
                    <input type="radio" checked={statusFilter === val} readOnly className="rounded-full border-slate-500" />
                    <span className="text-xs text-slate-200">{label}</span>
                  </label>
                ))}
                <div className="border-t border-slate-600 mt-1 pt-1">
                  <button onClick={() => setShowStatusPicker(false)} className="w-full px-2 py-1 text-[10px] text-slate-400 hover:text-white text-center">Close</button>
                </div>
              </div>
            )}
          </div>
          {/* Types dropdown with checkboxes */}
          <div className="relative">
            <button onClick={() => setShowTypePicker(!showTypePicker)}
              className="w-full bg-slate-700 text-white text-xs rounded px-3 py-2 border border-slate-600 text-left">
              {typeFilters.length === 0 ? 'All Types' : typeFilters.map(t => t === 'autorb' ? 'AutoRb' : t.charAt(0).toUpperCase() + t.slice(1)).join(', ')}
            </button>
            {showTypePicker && (
              <div className="absolute z-20 mt-1 w-48 bg-slate-700 border border-slate-600 rounded-lg shadow-xl p-2">
                {[['rebill', 'Rebill'], ['autorb', 'AutoRb'], ['recycle', 'Recycle']].map(([val, label]) => (
                  <label key={val} className="flex items-center space-x-2 px-2 py-1.5 hover:bg-slate-600 rounded cursor-pointer">
                    <input type="checkbox" checked={typeFilters.includes(val)}
                      onChange={() => setTypeFilters(p => p.includes(val) ? p.filter(x => x !== val) : [...p, val])}
                      className="rounded border-slate-500" />
                    <span className="text-xs text-slate-200">{label}</span>
                  </label>
                ))}
                <div className="border-t border-slate-600 mt-1 pt-1">
                  <button onClick={() => setShowTypePicker(false)} className="w-full px-2 py-1 text-[10px] text-slate-400 hover:text-white text-center">Close</button>
                </div>
              </div>
            )}
          </div>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-slate-700 text-white text-xs rounded px-3 py-2 border border-slate-600" />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-slate-700 text-white text-xs rounded px-3 py-2 border border-slate-600" />
        </div>
      </div>

      {/* Client Detail Panel */}
      {selectedClient && ci && (
        <div className="bg-slate-800 rounded-lg p-4 border border-blue-500/50 space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-bold text-blue-400">📋 Client #{ci.customer_id || ci.id} — Complete Profile</h3>
              {ci.deleted && <span className="px-2 py-0.5 rounded text-xs bg-red-800/30 text-red-300 border border-red-700/50">⛔ DELETED — Hard Decline in Recycle</span>}
            </div>
            <button onClick={() => { setSelectedClient(null); setClientData(null); }} className="text-xs text-slate-400 hover:text-white">✕ Close</button>
          </div>

          {/* Client Info */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            {[['First Name', ci.first_name], ['Last Name', ci.last_name], ['Customer ID', ci.customer_id || 'N/A'], ['Email', ci.email || 'N/A'], ['Phone', ci.phone_number || 'N/A'],
              ['Address', ci.address || 'N/A'], ['Address 2', ci.address2 || 'N/A'], ['City', ci.city || 'N/A'], ['ZIP', ci.zip || 'N/A'], ['State', ci.state || 'N/A'],
              ['Country', ci.country_code || 'N/A'], ['IP Address', ci.ip_address || 'N/A'], ['Card Number', ci.card_number || 'N/A'], ['First 6', ci.card_first6 || 'N/A'], ['Last 4', ci.card_last4 || 'N/A'],
              ['Expiry', ci.card_expiry || 'N/A'], ['Card Type', ci.card_type || 'N/A'], ['Card BIN', ci.card_bin || 'N/A'], ['Initial Amount', fmt(ci.initial_amount)],
              ['Rebill Cycle', ci.rebill_cycle === 0 ? 'Never Billed' : `Cycle ${ci.rebill_cycle}`], ['Rebill Status', ci.rebill_status || 'N/A'],
            ].map(([l, v], i) => (<div key={i} className="bg-slate-700/50 rounded px-2 py-1.5"><div className="text-[10px] text-slate-500">{l}</div><div className="text-slate-200 font-mono truncate" title={String(v)}>{v}</div></div>))}
          </div>

          {/* Status Summary */}
          <div className="bg-slate-700/30 rounded-lg p-3 space-y-1">
            <h4 className="text-xs font-bold text-slate-300">📌 Client Status</h4>
            <div className="flex flex-wrap gap-2 text-xs">
              {ci.deleted ? (
                <span className="px-2 py-1 rounded bg-red-800/20 text-red-300">⛔ DELETED — Removed after hard decline in Recycle Billing</span>
              ) : (
                <span className={`px-2 py-1 rounded ${ci.rebill_status === 'pending' ? 'bg-green-500/10 text-green-400' : ci.rebill_status === 'declined' ? 'bg-red-500/10 text-red-400' : ci.rebill_status === 'no_gateway_available' ? 'bg-orange-500/10 text-orange-400' : ci.rebill_status === 'scheduled' ? 'bg-blue-500/10 text-blue-400' : 'bg-slate-600 text-slate-300'}`}>
                  Status: {ci.rebill_status}
                </span>
              )}
              <span className="px-2 py-1 rounded bg-slate-600 text-slate-200">
                Cycle: {ci.rebill_cycle === null ? 'N/A' : ci.rebill_cycle === 0 ? 'Never Billed' : `Cycle ${ci.rebill_cycle}`}
              </span>
              {cRecycle.length > 0 && (() => {
                const active = cRecycle.find(r => !['success_recovered', 'ultimate_declined', 'failed'].includes(r.retry_status));
                const recovered = cRecycle.find(r => r.retry_status === 'success_recovered');
                const ultimate = cRecycle.find(r => r.retry_status === 'ultimate_declined');
                return (<>
                  {active && <span className="px-2 py-1 rounded bg-orange-500/10 text-orange-400">♻️ In Recycle Billing (cycle {active.retry_count}/{active.max_retries})</span>}
                  {active && <span className="px-2 py-1 rounded bg-blue-500/10 text-blue-400">Next Retry: {active.next_retry_at ? fmtD(active.next_retry_at) : 'Pending'}</span>}
                  {recovered && <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-400">✅ Recovered on {fmtD(recovered.last_retry_at || recovered.created_at)}</span>}
                  {ultimate && <span className="px-2 py-1 rounded bg-red-800/20 text-red-300">⛔ Ultimate Declined</span>}
                </>);
              })()}
            </div>
          </div>

          {/* All Processor-Bank-DBA Triplets */}
          {cTrips.length > 0 && (<div>
            <h4 className="text-xs font-bold text-slate-300 mb-1">🔗 All Processor - Bank - DBA Used ({cTrips.length})</h4>
            <div className="flex flex-wrap gap-1">
              {cTrips.map((t, i) => (<span key={i} className="px-2 py-1 rounded text-[10px] bg-slate-700 text-slate-300 border border-slate-600">
                {t.processor} - {t.bank} - {t.dba}{t.company ? ` - ${t.company}` : ''} <span className="text-slate-500">({t.source})</span>
              </span>))}
            </div>
          </div>)}

          {/* All CRM Transactions */}
          <div>
            <h4 className="text-xs font-bold text-slate-300 mb-1">📊 All CRM Transactions ({cTxns.length})</h4>
            <div className="overflow-x-auto"><table className="w-full text-xs">
              <thead><tr className="text-slate-500 border-b border-slate-700">
                <th className="text-left py-1 px-2">Date</th><th className="text-left py-1 px-2">Type</th><th className="text-left py-1 px-2">Job</th><th className="text-left py-1 px-2">Gateway</th>
                <th className="text-right py-1 px-2">Amount</th><th className="text-left py-1 px-2">Status</th><th className="text-left py-1 px-2">Code</th><th className="text-left py-1 px-2">NMI Response</th><th className="text-left py-1 px-2">TxnID</th>
              </tr></thead>
              <tbody>{cTxns.map(tx => (<tr key={tx.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                <td className="py-1 px-2 text-slate-300">{fmtD(tx.created_at)}</td>
                <td className="py-1 px-2">{tBadge(tx.type)}</td>
                <td className="py-1 px-2 text-cyan-400 text-[10px]">{tx.job_name || '-'}</td>
                <td className="py-1 px-2 text-slate-300 text-[10px]">{tx.processor}-{tx.bank}-{tx.dba}</td>
                <td className="py-1 px-2 text-right text-white font-mono">{fmt(tx.amount)}</td>
                <td className="py-1 px-2">{sBadge(tx.status)}</td>
                <td className="py-1 px-2 text-orange-400 font-mono">{tx.response_code || '-'}</td>
                <td className="py-1 px-2 text-slate-400 max-w-[150px] truncate" title={tx.response_text}>{tx.response_text || '-'}</td>
                <td className="py-1 px-2 text-slate-500 font-mono text-[10px]">{tx.transaction_id_nmi || '-'}</td>
              </tr>))}{cTxns.length === 0 && <tr><td colSpan={9} className="text-center py-3 text-slate-500">No CRM transactions</td></tr>}</tbody>
            </table></div>
          </div>

          {/* Recycle Billing Entries */}
          {cRecycle.length > 0 && (<div>
            <h4 className="text-xs font-bold text-orange-400 mb-1">♻️ Recycle Billing Attempts ({cRecycle.length})</h4>
            <div className="overflow-x-auto"><table className="w-full text-xs">
              <thead><tr className="text-slate-500 border-b border-slate-700">
                <th className="text-left py-1 px-2">Date</th><th className="text-left py-1 px-2">Source</th><th className="text-left py-1 px-2">Reason</th>
                <th className="text-left py-1 px-2">Original Gateway</th><th className="text-right py-1 px-2">Amount</th><th className="text-center py-1 px-2">Cycle</th><th className="text-left py-1 px-2">Status</th><th className="text-left py-1 px-2">Next Retry</th>
              </tr></thead>
              <tbody>{cRecycle.map(r => (<tr key={r.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                <td className="py-1 px-2 text-slate-300">{fmtD(r.created_at)}</td>
                <td className="py-1 px-2"><span className={`px-1.5 py-0.5 rounded text-[10px] ${r.source === 'rebill_nmi' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>{r.source === 'rebill_nmi' ? 'Rebill' : 'AutoRb'}</span></td>
                <td className="py-1 px-2 text-orange-400 text-[10px]">{r.decline_reason}</td>
                <td className="py-1 px-2 text-slate-400 text-[10px]">{r.original_gateway}</td>
                <td className="py-1 px-2 text-right text-orange-400 font-mono">{fmt(r.initial_amount)}</td>
                <td className="py-1 px-2 text-center text-slate-300">{r.retry_count}/{r.max_retries}</td>
                <td className="py-1 px-2">{rBadge(r.retry_status)}</td>
                <td className="py-1 px-2 text-slate-400 text-[10px]">{r.next_retry_at ? fmtD(r.next_retry_at) : (r.last_retry_at ? `Last: ${fmtD(r.last_retry_at)}` : '—')}</td>
              </tr>))}</tbody>
            </table></div>
          </div>)}
        </div>
      )}

      {/* Transactions Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="px-4 py-2 border-b border-slate-700 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <span className="text-xs text-slate-400">{total} transactions</span>
            <select value={perPage} onChange={e => setPerPage(parseInt(e.target.value))} className="bg-slate-700 text-white text-[10px] rounded px-2 py-1 border border-slate-600">
              {[25, 100, 250, 500].map(n => <option key={n} value={n}>{n}/page</option>)}</select>
          </div>
          <div className="flex items-center space-x-2">
            {loading && <span className="text-xs text-blue-400 animate-pulse">Loading...</span>}
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-[10px] disabled:opacity-30">← Prev</button>
            <span className="text-[10px] text-slate-400">{page + 1}/{totalPages || 1}</span>
            <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-[10px] disabled:opacity-30">Next →</button>
          </div>
        </div>
        <div className="overflow-x-auto"><table className="w-full text-xs">
          <thead><tr className="text-slate-500 border-b border-slate-700">
            <th className="text-left py-2 px-3">Date</th><th className="text-left py-2 px-3">Customer ID</th><th className="text-left py-2 px-3">Client</th>
            <th className="text-left py-2 px-3">Card</th><th className="text-left py-2 px-3">Type</th><th className="text-left py-2 px-3">Gateway</th>
            <th className="text-right py-2 px-3">Amount</th><th className="text-left py-2 px-3">Status</th><th className="text-left py-2 px-3">NMI Response</th><th className="text-left py-2 px-3">TxnID</th><th className="text-center py-2 px-3"></th>
          </tr></thead>
          <tbody>
            {transactions.map(tx => (<tr key={tx.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
              <td className="py-2 px-3 text-slate-300">{fmtD(tx.created_at)}</td>
              <td className="py-2 px-3"><button onClick={() => handleClientClick(tx.client_id)} className={`font-mono text-xs hover:underline ${selectedClient === tx.client_id ? 'text-blue-400 font-bold' : 'text-cyan-400'}`}>#{tx.customer_id || tx.client_id}</button></td>
              <td className="py-2 px-3 text-white">{tx.client_name}</td>
              <td className="py-2 px-3 text-slate-400 font-mono">{tx.card_first6}...{tx.card_last4}</td>
              <td className="py-2 px-3">{tBadge(tx.type)}</td>
              <td className="py-2 px-3 text-slate-300 max-w-[160px] truncate" title={tx.gateway_name}>{tx.gateway_name}</td>
              <td className="py-2 px-3 text-right text-white font-mono font-medium">{fmt(tx.amount)}</td>
              <td className="py-2 px-3">{sBadge(tx.status)}</td>
              <td className="py-2 px-3 text-slate-400 max-w-[150px] truncate" title={`${tx.response_code}: ${tx.response_text}`}>
                {tx.response_code && <span className="text-orange-400 mr-1">[{tx.response_code}]</span>}{tx.response_text}</td>
              <td className="py-2 px-3 text-slate-500 font-mono text-[10px]">{tx.transaction_id_nmi || '-'}</td>
              <td className="py-2 px-3 text-center">{tx.status === 'success' && tx.transaction_id_nmi ? <button onClick={() => handleRefund(tx.id, tx.transaction_id_nmi, tx.amount)} disabled={refunding === tx.id} className="px-2 py-0.5 bg-red-600/80 hover:bg-red-600 text-white rounded text-[10px] disabled:opacity-50">{refunding === tx.id ? '⏳' : '↩ Refund'}</button> : tx.status === 'refunded' ? <span className="text-[10px] text-purple-400">Refunded</span> : null}</td>
            </tr>))}
            {transactions.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-slate-500">No transactions found</td></tr>}
          </tbody>
        </table></div>
        {totalPages > 1 && (<div className="px-4 py-2 border-t border-slate-700 flex justify-end items-center space-x-2">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-[10px] disabled:opacity-30">← Prev</button>
          <span className="text-[10px] text-slate-400">{page + 1}/{totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-[10px] disabled:opacity-30">Next →</button>
        </div>)}
      </div>
    </div>
  );
}
